import { getHowToWinBannerDismissedCookie } from '@/redux/modules/cookies';
import { getTotalBidsPlaced, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { getTotalWonItemsCount } from '@/redux/modules/wonItemsCount/wonItemsCount.selectors';
import { useAppSelector } from '@/redux/hooks';
import { useHowToWinBannerExperiment } from '@/utils/amplitude/experiments/hooks/useHowToWinBannerExperiment.experiment';

export type HowToWinBannerVariant = 'big' | 'slim';

export type HowToWinBannerDisplayResult = {
    isInExperiment: boolean;
    shouldShow: boolean;
    showCloseButton: boolean;
    variant: HowToWinBannerVariant;
};

/**
 * Determines if the "How To Win" banner should be shown and which variant to display.
 *
 * Banner shows when:
 * - User is in experiment treatment
 * - User is in target audience (0 wins OR 0 bids)
 * - For logged-in users: banner has not been dismissed
 *
 */
export const useHowToWinBannerDisplay = (allowBigBanner = false): HowToWinBannerDisplayResult => {
    const isInTreatment = useHowToWinBannerExperiment();
    const isLoggedIn = useAppSelector(isUserLoggedIn);
    const totalBidsCount = useAppSelector(getTotalBidsPlaced);
    const wonItemsCount = useAppSelector(getTotalWonItemsCount);
    const bannerDismissed = useAppSelector(getHowToWinBannerDismissedCookie);

    const isInTargetAudience = wonItemsCount === 0 || totalBidsCount === 0;

    const shouldShow = isInTreatment && isInTargetAudience && (!isLoggedIn || !bannerDismissed);

    const variant: HowToWinBannerVariant = allowBigBanner && !isLoggedIn ? 'big' : 'slim';
    const showCloseButton = isLoggedIn;
    const isInExperiment = isInTreatment && (isInTargetAudience || !isLoggedIn);

    return { isInExperiment, shouldShow, showCloseButton, variant };
};
